import { MenuOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, MenuItem, Menu as MuiMenu } from "@mui/material";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigateWithQueryParams } from "../Utilities";
import { useGetAccessMonitoringQuery } from "../../api/graphqlApi";
import { useKeycloak } from "../Keycloak";

export default function Menu({ invertColors }) {

    const { ROUTES: { HOME, LIVE, TIMELAPSE, ARCHIVE, MYSITE, ACCESS }, CAMERAS } = window.conf
    const { t } = useTranslation()
    const { getUsername } = useKeycloak()

    const { showAccess } = useGetAccessMonitoringQuery(undefined, {
        selectFromResult: ({ data, isSuccess }) => {
            if (!isSuccess) return { showAccess: false }

            const hasAnyData = (CAMERAS ?? []).some(elem => {
                const cameraId = elem?.id
                const availableCameras = data?.byCameraId?.[cameraId]?.length || 0
                return availableCameras > 0
            })

            const isNetcoAdmin = getUsername() === "netco-admin"

            return { showAccess: hasAnyData && isNetcoAdmin }
        }
    })

    const pages = useMemo(() => ([
        { name: t("pages.live"), ...HOME },
        { name: t("pages.timelapse"), ...TIMELAPSE },
        { name: t("pages.archive"), ...ARCHIVE },
        { name: t("pages.mysite"), ...MYSITE },
        ...(showAccess ? [{ name: t("pages.access.title"), ...ACCESS }] : [])
    ]), [t, HOME, TIMELAPSE, ARCHIVE, MYSITE, ACCESS, showAccess])

    const currentPath = window.location.pathname

    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedIndex, setSelectedIndex] = useState(currentPath === LIVE.path ? 0 : pages.findIndex(page => page.path === currentPath))

    const navigate = useNavigateWithQueryParams()
    const isOpen = Boolean(anchorEl)
    const popoverId = isOpen ? "menu-appbar" : undefined

    const handleOpen = (event) => {
        if (anchorEl !== event?.currentTarget) {
            setAnchorEl(event.currentTarget)
        }
    }

    const handleClose = () => {
        if (anchorEl) {
            setAnchorEl(null)
        }
    }

    const handleSelectMenuItem = (event, index) => {
        if (selectedIndex !== index) {
            setSelectedIndex(index)
            navigate(pages[index].path)
        }
        handleClose()
    }

    useEffect(() => {
        if (currentPath !== pages[selectedIndex]?.path) {
            setSelectedIndex(currentPath === LIVE.path ? 0 : pages.findIndex(page => page.path === currentPath))
        }
    }, [currentPath, selectedIndex]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Fragment>

            {/* desktop menu */}

            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                {pages.map((page, index) => (
                    <Button
                        key={index}
                        disabled={page.disabled}
                        onClick={event => handleSelectMenuItem(event, index)}
                        sx={{
                            textAlign: "center",
                            display: 'block',
                            color: index === selectedIndex
                                ? invertColors
                                    ? 'white'
                                    : 'primary.main'
                                : invertColors
                                    ? 'secondary.dark'
                                    : 'dimgray',
                            whiteSpace: 'nowrap',
                            fontSize: '1rem',
                        }}
                    >
                        {page.name}
                    </Button>
                ))}
            </Box>

            {/* mobile menu */}

            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>

                <IconButton
                    aria-describedby={popoverId}
                    sx={{
                        p: 0,
                        color: invertColors ? 'white' : 'primary.main'
                    }}
                    onClick={handleOpen}
                >
                    <MenuOutlined sx={{ fontSize: "1.75rem" }} />
                </IconButton>

                <MuiMenu
                    id={popoverId}
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    keepMounted
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    open={isOpen}
                    onClose={handleClose}
                    sx={{ display: { xs: 'block', md: 'none' } }}
                >
                    {pages.map((page, index) => (
                        <MenuItem
                            key={index}
                            disabled={page.disabled}
                            onClick={event => handleSelectMenuItem(event, index)}
                            selected={index === selectedIndex}
                            component="a"
                        >
                            {page.name}
                        </MenuItem>
                    ))}
                </MuiMenu>
            </Box>

        </Fragment>
    )
}